import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface BatteryAllocation {
  id: string;
  lead_id?: string;
  org_id?: string;
  battery_id?: string;
  battery_type_id?: string;
  status: 'pending' | 'confirmed' | 'geplaatst' | 'cancelled';
  allocated_by?: string;
  allocated_at: string;
  confirmed_at?: string;
  placed_at?: string;
  notes?: string;
  lead?: {
    id: string;
    summary?: string;
    contact_id?: string;
  };
  battery?: {
    id: string;
    capacity_kwh: number;
    power_kw: number;
  };
  org_name?: string;
}

export const useBatteryAllocations = () => {
  const [allocations, setAllocations] = useState<BatteryAllocation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAllocations = async () => {
    const { data, error } = await supabase
      .from('battery_allocations')
      .select(`
        *,
        lead:leads(id, summary, contact_id),
        battery:batteries(id, capacity_kwh, power_kw),
        organization:organizations(name)
      `)
      .order('allocated_at', { ascending: false });

    if (error) {
      toast.error('Fout bij ophalen allocaties');
      return [];
    }
    
    // Map org name to flat structure
    return (data || []).map(allocation => ({
      ...allocation,
      org_name: allocation.organization?.name,
    }));
  };

  const loadData = async () => {
    setLoading(true);
    const data = await fetchAllocations();
    setAllocations(data);
    setLoading(false);
  };

  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('allocations-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'battery_allocations' }, () => {
        fetchAllocations().then(setAllocations);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const createAllocation = async (
    leadId: string,
    batteryId?: string,
    batteryTypeId?: string,
    notes?: string
  ) => {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('battery_allocations')
      .insert([{
        lead_id: leadId,
        battery_id: batteryId,
        battery_type_id: batteryTypeId,
        status: 'pending',
        allocated_by: userData.user?.id,
        notes,
      }])
      .select()
      .single();

    if (error) {
      toast.error('Fout bij aanmaken allocatie');
      throw error;
    }

    // Update battery status if allocated to specific battery
    if (batteryId) {
      await supabase
        .from('batteries')
        .update({ status: 'gereserveerd' })
        .eq('id', batteryId);
    }

    toast.success('Batterij gealloceerd aan lead');
    return data;
  };

  const updateAllocationStatus = async (
    allocationId: string,
    status: BatteryAllocation['status']
  ) => {
    const updates: any = { status };
    
    if (status === 'confirmed') {
      updates.confirmed_at = new Date().toISOString();
    } else if (status === 'geplaatst') {
      updates.placed_at = new Date().toISOString();
    } else if (status === 'cancelled') {
      updates.cancelled_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('battery_allocations')
      .update(updates)
      .eq('id', allocationId);

    if (error) {
      toast.error('Fout bij updaten allocatie status');
      throw error;
    }

    // If cancelled, update battery status back to available
    if (status === 'cancelled') {
      const allocation = allocations.find(a => a.id === allocationId);
      if (allocation?.battery_id) {
        await supabase
          .from('batteries')
          .update({ status: 'vrij' })
          .eq('id', allocation.battery_id);
      }
    }

    toast.success('Allocatie status gewijzigd');
  };

  const deleteAllocation = async (allocationId: string) => {
    const allocation = allocations.find(a => a.id === allocationId);
    
    const { error } = await supabase
      .from('battery_allocations')
      .delete()
      .eq('id', allocationId);

    if (error) {
      toast.error('Fout bij verwijderen allocatie');
      throw error;
    }

    // Update battery status back to available
    if (allocation?.battery_id) {
      await supabase
        .from('batteries')
        .update({ status: 'vrij' })
        .eq('id', allocation.battery_id);
    }

    toast.success('Allocatie verwijderd');
  };

  return {
    allocations,
    loading,
    refetch: loadData,
    createAllocation,
    updateAllocationStatus,
    deleteAllocation,
  };
};
